/**
 * The human QA boundary, as a rule rather than a convention.
 *
 * PROCESSING_PIPELINE.md's "Human QA boundary" section: review is **mandatory** on a raster
 * page, because reconstructed geometry is probabilistic and a poor scan can produce walls
 * that were never on the sheet. It is **optional** on a vector page, where the linework is
 * the source drawing's own geometry and the failure mode is exclusion of real content rather
 * than invention of false content.
 *
 * The desktop application enforces this in `ExportButton_Click` by refusing to export a
 * raster page with no reviewed recognition result in hand. Nothing enforced it in the web
 * application until packaging existed to enforce it at, which is why it lives here and is
 * checked before a package is written.
 */

/** The engine's SourceDocumentType, as it appears in the result JSON. */
const VECTOR = "Vector";

/**
 * Whether the operator must confirm they inspected the geometry before a package is created.
 *
 * Mandatory for anything that is not exactly `Vector`, which covers `Raster`, `Mixed` and
 * `RasterVectorized` - all three involve reconstruction - and also covers a missing or
 * unrecognised value. Defaulting an unknown source to "review required" is the safe
 * direction: the cost of being wrong is one extra confirmation, against a drawing built from
 * geometry nobody looked at.
 */
export function reviewIsMandatory(sourceDocumentType: unknown): boolean {
  return sourceDocumentType !== VECTOR;
}

/**
 * `sourceDocumentType` off a recognition result, without asserting anything else about it.
 *
 * The result is embedded in the package untouched and is otherwise opaque to this module -
 * see PackagePage.recognition. This is the single field any rule here reads.
 */
export function sourceDocumentTypeOf(recognition: unknown): string | null {
  if (typeof recognition !== "object" || recognition === null || Array.isArray(recognition)) {
    return null;
  }
  const value = (recognition as Record<string, unknown>)["sourceDocumentType"];
  return typeof value === "string" ? value : null;
}

/**
 * The engine's caution about this page, or null. Set on pages it converts less well, such as a
 * slide-style schematic export. It never blocks packaging; the reviewer is told and decides.
 */
export function recognitionWarningOf(recognition: unknown): string | null {
  if (typeof recognition !== "object" || recognition === null || Array.isArray(recognition)) return null;
  const warning = (recognition as Record<string, unknown>)["warning"];
  return typeof warning === "string" && warning.trim() !== "" ? warning : null;
}
